import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Snackbar, Alert, Slide } from "@mui/material";
import { setNotificationHandlers, clearNotificationHandlers } from "./notifyBridge";

const NotificationContext = createContext(null);

function SlideDown(props) {
  return <Slide {...props} direction="down" />;
}

export function NotificationProvider({ children }) {
  const [queue, setQueue] = useState([]);

  const remove = useCallback((id) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((severity, message, opts = {}) => {
    const id = opts.id ?? `${Date.now()}-${Math.random()}`;
    const duration = opts.duration ?? 4000;
    setQueue((prev) => {
      // Dedupe by (severity, message). If N parallel API calls all fail
      // with the same 403/500, the axios interceptor fires notify.error
      // N times — without this the user sees the same toast stacked N
      // deep on the top of the screen (see the multi-query MyReservations
      // page). Callers can bypass by passing an explicit opts.id.
      if (!opts.id && prev.some((item) =>
        item.severity === severity && item.message === message
      )) {
        return prev;
      }
      return [...prev, { id, severity, message, duration }];
    });
    return id;
  }, []);

  const value = useMemo(
    () => ({
      notifySuccess: (msg, opts) => notify("success", msg, opts),
      notifyError: (msg, opts) => notify("error", msg, opts),
      notifyInfo: (msg, opts) => notify("info", msg, opts),
      notifyWarning: (msg, opts) => notify("warning", msg, opts),
    }),
    [notify]
  );

  // Register with the module-level notifyBridge so non-React code (e.g. the
  // axios response interceptor) can fire toasts via notify.error()/etc.
  useEffect(() => {
    setNotificationHandlers({
      success: (msg, opts) => notify("success", msg, opts),
      error:   (msg, opts) => notify("error",   msg, opts),
      info:    (msg, opts) => notify("info",    msg, opts),
      warning: (msg, opts) => notify("warning", msg, opts),
    });
    return () => {
      clearNotificationHandlers();
    };
  }, [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {queue.map((item, idx) => (
        // Each Snackbar is positioned fixed at top-center, so concurrent
        // toasts overlap unless we offset them. Push each one ~64px below
        // the previous so they stack vertically instead of stomping each
        // other. `!important` is required because MUI sets the inline
        // `top` value programmatically based on the anchorOrigin.
        <Snackbar
          key={item.id}
          open
          autoHideDuration={item.duration}
          onClose={() => remove(item.id)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            top: `${24 + idx * 64}px !important`,
          }}
          slots={{
            transition: SlideDown
          }}
        >
          <Alert onClose={() => remove(item.id)} severity={item.severity} variant="filled">
            {item.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
